import { fakePackRepack } from 'constants/fakePackRepack';
import { fakeSample } from 'constants/fakeSample';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { PlayerSource } from '@landr/react-player';
import { StreamEntityType } from 'types/enums';
import { ApiResponses, ApiQueries, ApiQueryVariables } from '../types';
import { OnError } from './useError';

const isValidStreamId = (id?: string) =>
    !!id && id !== fakePackRepack.guid && id !== fakeSample.guid;

const useStream = ({
    id,
    streamEntityType,
    getIsExpired,
    onError,
    seek,
    setPlaying,
    setSources,
    updateExpirationDate,
    setLoading,
}: {
    id?: string;
    streamEntityType: StreamEntityType;
    getIsExpired: () => boolean;
    onError: OnError;
    seek: (value: number, playOnSeek?: boolean | undefined) => void;
    setPlaying: (value: boolean) => void;
    setSources: (value: PlayerSource[]) => void;
    updateExpirationDate: () => void;
    setLoading: (value: boolean) => void;
}) => {
    const [canUpdateSources, setCanUpdateSources] = useState(false);
    const lazySources = useRef<PlayerSource[]>([]);

    const handleGetStreamCompleted = useCallback(
        (data) => {
            const streamUrl = data[ApiResponses[streamEntityType]]?.url;

            if (id && streamUrl) {
                const newSources = [{ id, url: streamUrl, preload: true }];

                if (canUpdateSources) {
                    setSources(newSources);
                } else {
                    lazySources.current = newSources;
                }

                updateExpirationDate();
            }
        },
        [
            canUpdateSources,
            setSources,
            streamEntityType,
            id,
            updateExpirationDate,
        ],
    );

    const handleGetStreamError = useCallback(
        (error, withNotifications = false) => {
            onError({
                error: error,
                errorMessage: `Failed to fetch a ${streamEntityType} stream URL`,
                extraArguments: {
                    id: id ?? 'undefined',
                },
                withNotifications: withNotifications,
            });
        },
        [onError, id, streamEntityType],
    );

    const [getStream] = useLazyQuery(ApiQueries[streamEntityType], {
        fetchPolicy: 'no-cache',
        onCompleted: handleGetStreamCompleted,
        onError: handleGetStreamError,
    });

    useEffect(() => {
        isValidStreamId(id) &&
            getStream({
                variables: {
                    [ApiQueryVariables[streamEntityType]]: id,
                },
            });
    }, [getStream, streamEntityType, id]);

    const handlePlay = useCallback(
        (newTime?: number) =>
            Number.isFinite(newTime)
                ? seek(newTime as number, true)
                : setPlaying(true),
        [seek, setPlaying],
    );

    const handleGetStreamAndPlay = useCallback(
        async (newTime?: number) => {
            await getStream({
                variables: {
                    [ApiQueryVariables[streamEntityType]]: id,
                },
                onError: (error) => handleGetStreamError(error, true),
            });

            handlePlay(newTime);
        },
        [getStream, handleGetStreamError, handlePlay, id, streamEntityType],
    );

    const playStream = useCallback(
        (newTime?: number) => {
            if (!id || !isValidStreamId(id)) {
                onError({
                    errorMessage: `playStream was invoked with invalid value`,
                    error: new Error(
                        `playStream was invoked with invalid value`,
                    ),
                    extraArguments: {
                        id: id || 'undefined',
                    },
                });
                return;
            }

            const hasLazySources = lazySources.current.length > 0;

            if (!canUpdateSources) {
                setCanUpdateSources(true);
                hasLazySources && setSources(lazySources.current);
            }

            if (!hasLazySources || getIsExpired()) {
                setLoading(true);
                handleGetStreamAndPlay(newTime);
            } else {
                handlePlay(newTime);
            }
        },
        [
            handleGetStreamAndPlay,
            handlePlay,
            onError,
            id,
            getIsExpired,
            setLoading,
            canUpdateSources,
            setCanUpdateSources,
            setSources,
        ],
    );

    return {
        playStream,
    };
};

export default useStream;
